import React, { useEffect, useState } from 'react'
import {Plus, Edit, Trash2, Search, Video as VideoIcon, Mic, FileText} from 'lucide-react'
import { useEntityCRUD } from '../../hooks/useEntityCRUD'
import { useAdminLog } from '../../hooks/useAdminLog'
import { lumi } from '../../lib/lumi'
import AdminLayout from '../../components/admin/AdminLayout'
import toast from 'react-hot-toast'

const SermonsManagement: React.FC = () => {
  const { data: sermons, loading, fetchData, createRecord, updateRecord, deleteRecord } = useEntityCRUD('sermons')
  const { logAction } = useAdminLog()
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingSermon, setEditingSermon] = useState<any>(null)
  const [formData, setFormData] = useState({
    title: '',
    speaker: '',
    description: '',
    date: '',
    type: 'VIDEO',
    videoUrl: '',
    audioUrl: '',
    textContent: '',
    thumbnailUrl: ''
  })

  useEffect(() => {
    fetchData({ sort: { date: -1 } })
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const user = lumi.auth.user
    if (!user) return

    try {
      const sermonData = {
        ...formData,
        creator: user.userId,
        createdAt: editingSermon?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      if (editingSermon) {
        await updateRecord(editingSermon._id, sermonData)
        await logAction('UPDATE', 'sermons', editingSermon._id, `Mensagem atualizada: ${formData.title}`)
        toast.success('Mensagem atualizada com sucesso!')
      } else {
        const newSermon = await createRecord(sermonData)
        await logAction('CREATE', 'sermons', newSermon._id, `Mensagem criada: ${formData.title}`)
        toast.success('Mensagem criada com sucesso!')
      }
      
      await fetchData({ sort: { date: -1 } })
      setShowModal(false)
      setEditingSermon(null)
      resetForm()
    } catch (error) {
      console.error('Failed to save sermon:', error)
    }
  }

  const handleEdit = (sermon: any) => {
    setEditingSermon(sermon)
    setFormData({
      title: sermon.title,
      speaker: sermon.speaker,
      description: sermon.description || '',
      date: sermon.date?.split('T')[0] || '',
      type: sermon.type,
      videoUrl: sermon.videoUrl || '',
      audioUrl: sermon.audioUrl || '',
      textContent: sermon.textContent || '',
      thumbnailUrl: sermon.thumbnailUrl || ''
    })
    setShowModal(true)
  }

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Tem certeza que deseja excluir a mensagem "${title}"?`)) return
    
    try {
      await deleteRecord(id)
      await logAction('DELETE', 'sermons', id, `Mensagem excluída: ${title}`)
      toast.success('Mensagem excluída com sucesso!')
      await fetchData({ sort: { date: -1 } })
    } catch (error) {
      console.error('Failed to delete:', error)
      toast.error('Erro ao excluir mensagem')
    }
  }

  const resetForm = () => {
    setFormData({
      title: '',
      speaker: '',
      description: '',
      date: '',
      type: 'VIDEO',
      videoUrl: '',
      audioUrl: '',
      textContent: '',
      thumbnailUrl: ''
    })
  }

  const filteredSermons = sermons.filter(sermon =>
    sermon.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sermon.speaker?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'VIDEO': return <VideoIcon size={16} />
      case 'AUDIO': return <Mic size={16} />
      case 'TEXT': return <FileText size={16} />
      default: return null
    }
  }

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gerenciar Palavra de Deus</h1>
          <p className="text-gray-600">Adicione, edite e remova mensagens</p>
        </div>
        <button
          onClick={() => {
            setEditingSermon(null)
            resetForm()
            setShowModal(true)
          }}
          className="flex items-center gap-2 px-6 py-3 bg-[#1e3a8a] text-white rounded-lg hover:bg-[#2d4ba5] transition-colors"
        >
          <Plus size={20} /> Nova Mensagem
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por título ou ministrador..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a8a]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSermons.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              Nenhuma mensagem encontrada
            </div>
          ) : (
            filteredSermons.map((sermon: any) => (
              <div key={sermon._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {sermon.thumbnailUrl && (
                  <img src={sermon.thumbnailUrl} alt={sermon.title} className="w-full h-48 object-cover" />
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {getTypeIcon(sermon.type)}
                    <span className="text-xs font-semibold text-gray-500">{sermon.type}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{sermon.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{sermon.speaker}</p>
                  {sermon.description && (
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{sermon.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mb-4">
                    {new Date(sermon.date).toLocaleDateString('pt-BR')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(sermon)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Edit size={16} /> Editar
                    </button>
                    <button
                      onClick={() => handleDelete(sermon._id, sermon.title)}
                      className="flex items-center justify-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingSermon ? 'Editar Mensagem' : 'Nova Mensagem'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ministrador *</label>
                <input
                  type="text"
                  required
                  value={formData.speaker}
                  onChange={(e) => setFormData({ ...formData, speaker: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data *</label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo *</label>
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                  >
                    <option value="VIDEO">Vídeo</option>
                    <option value="AUDIO">Áudio</option>
                    <option value="TEXT">Texto</option>
                  </select>
                </div>
              </div>

              {formData.type === 'VIDEO' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">URL do Vídeo</label>
                  <input
                    type="url"
                    value={formData.videoUrl}
                    onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                  />
                </div>
              )}

              {formData.type === 'AUDIO' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">URL do Áudio</label>
                  <input
                    type="url"
                    value={formData.audioUrl}
                    onChange={(e) => setFormData({ ...formData, audioUrl: e.target.value })}
                    placeholder="https://example.com/audio.mp3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                  />
                </div>
              )}

              {formData.type === 'TEXT' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Conteúdo do Texto</label>
                  <textarea
                    value={formData.textContent}
                    onChange={(e) => setFormData({ ...formData, textContent: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL da Miniatura</label>
                <input
                  type="url"
                  value={formData.thumbnailUrl}
                  onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    setEditingSermon(null)
                    resetForm()
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-[#1e3a8a] text-white rounded-lg hover:bg-[#2d4ba5] transition-colors"
                >
                  {editingSermon ? 'Salvar Alterações' : 'Criar Mensagem'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default SermonsManagement